// sectionNav.ts — Lista única de secciones navegables + scroll centrado
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: fuente única de verdad para la navegación de secciones.
// Header y Footer importan este mismo SECTION_NAV en vez de mantener cada
// uno su propia lista de links — así siempre muestran exactamente las
// mismas opciones, con el mismo destino, sin que se puedan desincronizar
// con el tiempo. Los labels salen de t.nav[key] (i18n) en cada componente;
// aquí solo vive el orden y el href de cada sección.
// ════════════════════════════════════════════════════════════════════════

export interface SectionNavItem {
  /** Clave dentro de t.nav para el label traducido (ver translations.ts). */
  key: 'services' | 'roadmap' | 'faq' | 'contact';
  /** Ancla de la sección real (coincide con el id en su componente). */
  href: '#services' | '#roadmap' | '#faq' | '#form';
}

export const SECTION_NAV: readonly SectionNavItem[] = [
  { key: 'services', href: '#services' },
  { key: 'roadmap',  href: '#roadmap'  },
  { key: 'faq',      href: '#faq'      },
  { key: 'contact',  href: '#form'     },
];

// ── scrollToSection — scroll suave que CENTRA la sección en la pantalla ──
// El navegador, por defecto, al saltar a un ancla (#id) solo alinea el
// borde superior del elemento justo debajo del header fijo (ver
// `[id] { scroll-margin-top: 64px }` en styles.css). Acá en cambio
// calculamos la posición para que el CENTRO vertical de la sección quede
// en el centro vertical de la pantalla — mucho más cómodo de leer en
// secciones que no ocupan toda la altura del viewport.
//
// Si la sección es más alta que la pantalla, no hay forma de centrarla
// por completo (siempre se corta arriba o abajo) — en ese caso el cálculo
// simplemente prioriza que su mitad visible quede centrada, que es lo más
// parecido a "centrado" que se puede lograr.
export function scrollToSection(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const sectionCenter = rect.top + window.scrollY + rect.height / 2;
  const targetY = sectionCenter - window.innerHeight / 2;

  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const clampedY = Math.min(Math.max(targetY, 0), Math.max(maxScroll, 0));

  window.scrollTo({ top: clampedY, behavior: 'smooth' });

  // Actualiza el hash en la URL (para compartir el link, botón atrás,
  // etc.) SIN usar location.hash — eso dispara el salto nativo del
  // navegador (que pelearía con el scroll suave de arriba).
  if (window.history?.pushState) {
    window.history.pushState(null, '', `#${id}`);
  }
}
